import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import MasterCombobox from "@/components/MasterCombobox";

/**
 * Multilevel BOM — toggle a part as Assembly and attach child parts (each with qty).
 * Child unit costs roll into the parent's final_cost via backend totals.
 */
export default function AssemblyManager({ open, onOpenChange, part, onSaved }) {
    const navigate = useNavigate();
    const [isAssembly, setIsAssembly] = useState(!!part?.is_assembly);
    const [children, setChildren] = useState(part?.child_parts || []);
    const [allParts, setAllParts] = useState([]);

    useEffect(() => {
        if (!open) return;
        setIsAssembly(!!part?.is_assembly);
        setChildren(part?.child_parts || []);
        api.get("/parts").then((r) => setAllParts(r.data || []));
    }, [open, part]);

    const createChildPart = async (label) => {
        const r = await api.post("/parts", {
            part_name: label,
            is_child: true,
            project_years: 5,
            status: "Lead",
            segment: "Auto",
        });
        // Refresh local picker list with the new part
        setAllParts((prev) => [...prev, r.data]);
        return { id: r.data.part_id, label: r.data.part_name };
    };

    const pickItems = allParts
        .filter((p) => p.part_id !== part?.part_id)
        .map((p) => ({ id: p.part_id, label: p.part_name, sub: p.part_number || "" }));

    const addRow = () => setChildren([...children, { part_id: "", quantity: 1 }]);
    const updateRow = (i, patch) => setChildren(children.map((r, idx) => idx === i ? { ...r, ...patch } : r));
    const removeRow = (i) => setChildren(children.filter((_, idx) => idx !== i));

    const save = async () => {
        try {
            await api.put(`/parts/${part.part_id}`, {
                ...part,
                is_assembly: isAssembly,
                child_parts: children
                    .filter((c) => c.part_id)
                    .map((c) => ({ part_id: c.part_id, quantity: Number(c.quantity) || 1 })),
            });
            toast.success("Assembly settings saved");
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-none max-w-2xl">
                <DialogHeader><DialogTitle>Multi-level BOM · Assembly</DialogTitle></DialogHeader>

                <div className="flex items-center gap-3 border border-zinc-200 px-4 py-3">
                    <Label htmlFor="assembly-switch" className="text-[11px] uppercase tracking-wider text-zinc-500 cursor-pointer">
                        Mark this part as an Assembly (rolls up child part costs)
                    </Label>
                    <Switch id="assembly-switch" checked={isAssembly} onCheckedChange={setIsAssembly} data-testid="assembly-switch" />
                </div>

                {isAssembly && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <h4 className="text-xs uppercase tracking-wider text-zinc-500 font-mono">Child Parts</h4>
                            <Button variant="outline" size="sm" className="rounded-none h-7" onClick={addRow} data-testid="add-child-part">
                                <Plus size={12} className="mr-1" /> Add child
                            </Button>
                        </div>
                        {children.length === 0 && (
                            <div className="text-xs text-zinc-400 italic border border-dashed border-zinc-200 py-4 text-center">
                                No children yet. Click <em>Add child</em> to attach a sub-part.
                            </div>
                        )}
                        {children.map((c, i) => (
                            <div key={i} className="grid grid-cols-[1fr_80px_30px_30px] gap-2 items-center" data-testid={`child-row-${i}`}>
                                <MasterCombobox
                                    items={pickItems}
                                    value={c.part_id}
                                    placeholder="Pick / type to add"
                                    onChange={(id) => updateRow(i, { part_id: id })}
                                    onCreate={createChildPart}
                                    testIdPrefix={`child-pick-${i}`}
                                />
                                <Input
                                    type="number" min="0.01" step="1"
                                    value={c.quantity ?? 1}
                                    onChange={(e) => updateRow(i, { quantity: e.target.value })}
                                    className="h-8 rounded-none font-mono"
                                    placeholder="Qty"
                                    data-testid={`child-qty-${i}`}
                                />
                                <button
                                    onClick={() => { if (c.part_id) { onOpenChange(false); navigate(`/parts/${c.part_id}/costing`); } }}
                                    disabled={!c.part_id}
                                    title="Open child part costing"
                                    className="text-zinc-400 hover:text-[#002FA7] disabled:opacity-30 disabled:cursor-not-allowed"
                                    data-testid={`child-open-${i}`}
                                >
                                    <ExternalLink size={14} />
                                </button>
                                <button onClick={() => removeRow(i)} className="text-zinc-400 hover:text-[#E63946]" data-testid={`child-remove-${i}`}>
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                        <p className="text-[10px] text-zinc-500 mt-2">
                            Each child must have a <strong>selected draft</strong> on its Costing page — its <em>final cost / piece</em>
                            × quantity rolls into this part's totals.
                        </p>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227B]" onClick={save} data-testid="save-assembly">Save</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
